import {
  DeleteOutlined,
  EditOutlined,
  PlusOutlined,
  PrinterOutlined,
  SearchOutlined
} from "@ant-design/icons";
import {
  Alert,
  Button,
  Card,
  Col,
  DatePicker,
  Descriptions,
  Divider,
  Empty,
  Form,
  Input,
  InputNumber,
  message,
  Modal,
  Popconfirm,
  Row,
  Select,
  Space,
  Table,
  Tabs,
  Tag,
  Typography
} from "antd";
import { EntityAttachmentsPanel } from "../../shared/components/EntityAttachmentsPanel";
import dayjs from "dayjs";
import { printManifestStationery } from "../../shared/printing/transportPrintTemplates";

// ─── Print manifest in a clean pop-up window ─────────────────────────────────
function printManifest(manifest: ManifestDetail) {
  const rows = manifest.consignments;
  const totalPkgs = rows.reduce((s, r) => s + r.packages, 0);
  const totalWt   = rows.reduce((s, r) => s + r.actualWeight, 0);

  const rowsHtml = rows.map((r, i) => `
    <tr>
      <td>${i + 1}</td>
      <td>${r.consignmentNo}</td>
      <td>${dayjs(r.bookingDate).format("DD/MM/YY")}</td>
      <td>${r.destination}</td>
      <td>${r.consignor}</td>
      <td>${r.consignee}</td>
      <td class="num">${r.packages}</td>
      <td class="num">${r.actualWeight.toFixed(3)}</td>
      <td>${r.goodsName ?? ""}</td>
      <td>${r.eWayBillNo ?? ""}</td>
      <td>${r.privateMark ?? ""}</td>
      <td>${r.paymentType === "ToBeBilled" ? "TBB" : r.paymentType}</td>
      <td class="num">${r.freightAmount.toFixed(2)}</td>
    </tr>`).join("");

  const html = `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8"/>
  <title>Manifest — ${manifest.manifestNo}</title>
  <style>
    * { box-sizing: border-box; margin: 0; padding: 0; }
    body { font-family: Arial, sans-serif; font-size: 10px; color: #000; padding: 12mm; }
    h1 { font-size: 16px; text-align: center; margin-bottom: 4px; }
    .subtitle { text-align: center; font-size: 11px; margin-bottom: 10px; color: #444; }
    .header-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 4px 12px; border: 1px solid #000; padding: 6px 8px; margin-bottom: 10px; }
    .header-grid .field { display: flex; flex-direction: column; }
    .header-grid .label { font-size: 8px; color: #666; text-transform: uppercase; letter-spacing: 0.3px; }
    .header-grid .value { font-size: 11px; font-weight: 600; }
    table { width: 100%; border-collapse: collapse; margin-bottom: 10px; }
    th { background: #333; color: #fff; padding: 4px 5px; text-align: left; font-size: 9px; border: 1px solid #555; }
    td { padding: 3px 5px; border: 1px solid #ccc; font-size: 9px; vertical-align: top; }
    tr:nth-child(even) td { background: #f9f9f9; }
    .num { text-align: right; }
    tfoot td { font-weight: 700; background: #eee; border-top: 2px solid #000; }
    .footer { margin-top: 16px; display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 8px; }
    .sig-box { border-top: 1px solid #000; padding-top: 4px; font-size: 9px; text-align: center; color: #555; }
    @media print {
      body { padding: 8mm; }
      @page { margin: 8mm; size: A4 landscape; }
    }
  </style>
</head>
<body>
  <h1>LORRY MANIFEST</h1>
  <div class="subtitle">${manifest.manifestNo}</div>
  <div class="header-grid">
    <div class="field"><span class="label">Manifest No</span><span class="value">${manifest.manifestNo}</span></div>
    <div class="field"><span class="label">Date</span><span class="value">${dayjs(manifest.dispatchDate).format("DD/MM/YYYY")}</span></div>
    <div class="field"><span class="label">Lorry No</span><span class="value">${manifest.vehicleNo}</span></div>
    <div class="field"><span class="label">Mode</span><span class="value">${manifest.transportMode}</span></div>
    <div class="field"><span class="label">Driver</span><span class="value">${manifest.driverName ?? "—"}</span></div>
    <div class="field"><span class="label">Loaded By</span><span class="value">${manifest.loadedBy ?? "—"}</span></div>
    ${manifest.railwayReceiptNo ? `<div class="field"><span class="label">RR No</span><span class="value">${manifest.railwayReceiptNo}</span></div>` : ""}
    <div class="field"><span class="label">Total Consignments</span><span class="value">${rows.length}</span></div>
  </div>

  <table>
    <thead>
      <tr>
        <th>#</th><th>Consignment No</th><th>Date</th><th>Destination</th>
        <th>Consignor</th><th>Consignee</th><th>Pkgs</th><th>Weight (kg)</th>
        <th>Goods</th><th>E-Way Bill</th><th>Pvt. Mark</th><th>Payment</th><th>Freight (₹)</th>
      </tr>
    </thead>
    <tbody>${rowsHtml}</tbody>
    <tfoot>
      <tr>
        <td colspan="6"><strong>TOTAL</strong></td>
        <td class="num">${totalPkgs}</td>
        <td class="num">${totalWt.toFixed(3)}</td>
        <td colspan="5"></td>
      </tr>
    </tfoot>
  </table>

  <div class="footer">
    <div class="sig-box">Prepared By</div>
    <div class="sig-box">Checked By</div>
    <div class="sig-box">Authorised Signatory</div>
  </div>
</body>
</html>`;

  const win = window.open("", "_blank", "width=1100,height=750");
  if (!win) { alert("Allow pop-ups to print the manifest."); return; }
  win.document.write(html);
  win.document.close();
  win.onload = () => { win.focus(); win.print(); };
}
import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { transportApi } from "../../api/transportApi";
import { usePageKeyboardShortcuts } from "../../shared/hooks/usePageKeyboardShortcuts";
import { getDefaultContext } from "../../api/httpClient";
import { getApiErrorMessage } from "../../utils/apiError";
import { getEnteredByUser } from "../../utils/currentUser";
import { StaticDrawer } from "../../shared/components/StaticDrawer";
import { StaticModal } from "../../shared/components/StaticModal";
import { LookupSelect } from "../../shared/components/LookupSelect";
import { SaveActionButton } from "../../shared/components/SaveActionButton";
import { BranchCreateModal } from "../../shared/components/BranchCreateModal";
import { VehicleCreateModal } from "../../shared/components/VehicleCreateModal";
import { DriverCreateModal } from "../../shared/components/DriverCreateModal";
import type { LookupItem } from "../../shared/components/LookupModal";
import { useOperationsWorkspace } from "../../shared/components/OperationsTransactionTabs";
import { useAuthStore } from "../../stores/authStore";

type ManifestSummary = {
  id: number;
  manifestNo: string;
  dispatchDate: string;
  vehicleId: number;
  vehicleNo: string;
  driverId?: number | null;
  driverName?: string | null;
  loadedBy?: string | null;
  transportMode: string;
  railwayReceiptNo?: string | null;
  consignmentCount: number;
  totalPackages: number;
  totalWeight: number;
};

type ManifestDetail = ManifestSummary & {
  fromBranchId: number;
  toBranchId: number;
  hasArrival: boolean;
  consignments: ManifestConsignmentRow[];
  editApprovalStatus: "None" | "Pending" | "Approved";
  editRequestReason?: string | null;
  canEdit: boolean;
  canApproveEdit: boolean;
};

type ManifestConsignmentRow = {
  manifestConsignmentId: number;
  consignmentId: number;
  consignmentNo: string;
  bookingDate: string;
  destination: string;
  consignor: string;
  consignee: string;
  packages: number;
  actualWeight: number;
  chargedWeight: number;
  goodsName: string;
  eWayBillNo?: string | null;
  privateMark?: string | null;
  remarks?: string | null;
  paymentType: string;
  freightAmount: number;
};

const TRANSPORT_MODES = [
  { value: "Road", label: "Road" },
  { value: "Rail", label: "Train" }
];

const PAYMENT_COLORS: Record<string, string> = {
  ToPay: "orange",
  Paid: "green",
  ToBeBilled: "blue",
  FOC: "purple"
};

export const ManifestEntryPage = () => {
  const workspace = useOperationsWorkspace();
  const location = useLocation();
  const navigate = useNavigate();
  const sessionUser = useAuthStore((state) => state.user);
  const activeRole = useAuthStore((state) => state.activeRole);
  const isAdmin = [...(sessionUser?.roles ?? []), activeRole ?? ""].some((role) => ["admin", "administrator", "superadmin"].includes(role.toLowerCase()));
  const [manifests, setManifests] = useState<ManifestSummary[]>([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");

  const searchInputRef = useRef<any>(null);
  const newBtnRef = useRef<HTMLButtonElement>(null);

  // Create manifest drawer
  const [createOpen, setCreateOpen] = useState(false);
  const [createSaving, setCreateSaving] = useState(false);
  const [createTempKey, setCreateTempKey] = useState<string | null>(null);
  const [form] = Form.useForm();
  const [manualManifestNumberEnabled, setManualManifestNumberEnabled] = useState(true);
  const [useAutomaticManifestNumber, setUseAutomaticManifestNumber] = useState(false);
  const [availableManifestRanges, setAvailableManifestRanges] = useState<any[]>([]);
  const fromBranchId = Form.useWatch("fromBranchId", form);
  const selectedVehicleId = Form.useWatch("vehicleId", form);
  const [ownerName, setOwnerName] = useState("");
  const [eligibleOpen, setEligibleOpen] = useState(false);
  const [eligibleLoading, setEligibleLoading] = useState(false);
  const [eligibleRows, setEligibleRows] = useState<any[]>([]);
  const [selectedConsignmentIds, setSelectedConsignmentIds] = useState<number[]>([]);
  const [selectedConsignments, setSelectedConsignments] = useState<any[]>([]);
  const [draftSelectedConsignmentIds, setDraftSelectedConsignmentIds] = useState<number[]>([]);
  const [consignmentRemarks, setConsignmentRemarks] = useState<Record<number, string>>({});

  // Manifest detail drawer
  const [detailOpen, setDetailOpen] = useState(false);
  const [selectedManifest, setSelectedManifest] = useState<ManifestDetail | null>(null);
  const [editRequestOpen, setEditRequestOpen] = useState(false);
  const [editRequestReason, setEditRequestReason] = useState("");
  const [manifestEditOpen, setManifestEditOpen] = useState(false);
  const [manifestEditSaving, setManifestEditSaving] = useState(false);
  const [manifestEditForm] = Form.useForm();
  const editTransportMode = Form.useWatch("transportMode", manifestEditForm);
  const [detailLoading, setDetailLoading] = useState(false);

  // Detail drawer tab
  const [detailTab, setDetailTab] = useState("details");

  // Add consignment to manifest
  const [addCnmtOpen, setAddCnmtOpen] = useState(false);
  const [addCnmtSaving, setAddCnmtSaving] = useState(false);
  const [cnmtSearch, setCnmtSearch] = useState("");
  const [cnmtResults, setCnmtResults] = useState<any[]>([]);
  const [cnmtSearching, setCnmtSearching] = useState(false);

  // ── Inline create modals (vehicle / driver / branch) ─────────────────────
  type BranchTarget = "fromBranchId" | "toBranchId";
  const [vehicleCreateOpen, setVehicleCreateOpen] = useState(false);
  const [vehiclePrefill, setVehiclePrefill]       = useState("");
  const [vehicleSeeds, setVehicleSeeds]           = useState<LookupItem[]>([]);

  const [driverCreateOpen, setDriverCreateOpen]   = useState(false);
  const [driverPrefill, setDriverPrefill]         = useState("");
  const [driverSeeds, setDriverSeeds]             = useState<LookupItem[]>([]);

  const [branchCreateOpen, setBranchCreateOpen]   = useState(false);
  const [branchPrefill, setBranchPrefill]         = useState("");
  const [branchTarget, setBranchTarget]           = useState<BranchTarget>("fromBranchId");
  const [branchSeeds, setBranchSeeds]             = useState<LookupItem[]>([]);

  // All state declared — safe to derive composite flag
  const anyOverlayOpen = createOpen || detailOpen || addCnmtOpen
    || vehicleCreateOpen || driverCreateOpen || branchCreateOpen;

  // Auto-focus first input when create drawer opens
  useEffect(() => {
    if (!createOpen) return;
    const timer = setTimeout(() => {
      const drawer = document.querySelector(".static-overlay-drawer .ant-drawer-body");
      const first = drawer?.querySelector<HTMLElement>("input, .ant-select-selector");
      first?.focus();
    }, 120);
    return () => clearTimeout(timer);
  }, [createOpen]);

  usePageKeyboardShortcuts({
    "alt+n": () => openCreateManifest(),
    "f5":    () => void loadManifests(),
    "f3":    () => searchInputRef.current?.focus(),
  }, !anyOverlayOpen);

  usePageKeyboardShortcuts({
    "ctrl+s": () => void onCreate(),
    "alt+w":  () => cancelCreateManifest(),
  }, createOpen);

  const vehicleFetcher = useCallback(
    (q: string, _page: number, pageSize: number) => transportApi.searchVehicleLookups(q, pageSize),
    []
  );
  const driverFetcher = useCallback(
    (q: string, _page: number, pageSize: number) => transportApi.searchDriverLookups(q, pageSize),
    []
  );
  const branchFetcher = useCallback(
    (q: string, page: number, pageSize: number) => transportApi.searchBranchLookups(q, page, pageSize),
    []
  );
  const openCreateManifest = () => {
    form.resetFields();
    form.setFieldsValue({ transportMode: "Road", dispatchDate: dayjs() });
    setOwnerName("");
    setSelectedConsignmentIds([]);
    setSelectedConsignments([]);
    setDraftSelectedConsignmentIds([]);
    setConsignmentRemarks({});
    setEligibleRows([]);
    setUseAutomaticManifestNumber(false);
    setCreateTempKey(crypto.randomUUID());
    setCreateOpen(true);
  };

  useEffect(() => { if (workspace?.mode === "entry") openCreateManifest(); }, [workspace?.mode]);

  const cancelCreateManifest = () => {
    if (createTempKey) void transportApi.deleteAttachmentsByTempKey(createTempKey);
    setCreateTempKey(null);
    setCreateOpen(false);
    form.resetFields();
  };

  const loadManifests = async () => {
    setLoading(true);
    try {
      const result = await transportApi.listManifests({ q: search.trim() || undefined });
      setManifests((result.data ?? result) as ManifestSummary[]);
    } catch (error) {
      message.error(getApiErrorMessage(error, "Failed to load manifests"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (workspace?.mode === "data") void loadManifests(); }, [workspace?.mode]);

  useEffect(() => {
    transportApi.getAccountingSettings()
      .then((settings) => {
        setManualManifestNumberEnabled(settings.isManualManifestNumberEnabled !== false);
      })
      .catch(() => setManualManifestNumberEnabled(true));
  }, []);

  useEffect(() => {
    if (!createOpen || !fromBranchId) {
      setAvailableManifestRanges([]);
      return;
    }
    form.setFieldValue("manifestNo", undefined);
    transportApi.listAvailableBiltyNumberSeries(Number(fromBranchId), undefined, "Manifest")
      .then(async (ranges) => {
        setAvailableManifestRanges(ranges);
        if (ranges.length > 0) {
          setUseAutomaticManifestNumber(false);
          return;
        }
        const preview = await transportApi.getNextAutomaticManifestNumber(Number(fromBranchId));
        setUseAutomaticManifestNumber(true);
        form.setFieldValue("manifestNo", preview.number);
      })
      .catch((error) => {
        setAvailableManifestRanges([]);
        setUseAutomaticManifestNumber(false);
        message.error(getApiErrorMessage(error, "Unable to load the Manifest number"));
      });
  }, [createOpen, fromBranchId]);

  useEffect(() => {
    if (!createOpen || !fromBranchId) return;
    setEligibleLoading(true);
    setEligibleOpen(true);
    transportApi.listEligibleManifestConsignments(Number(fromBranchId))
      .then((rows) => {
        setEligibleRows(rows);
        setSelectedConsignmentIds([]);
        setSelectedConsignments([]);
        setConsignmentRemarks({});
        setDraftSelectedConsignmentIds([]);
      })
      .catch((error) => message.error(getApiErrorMessage(error, "Unable to load available consignments")))
      .finally(() => setEligibleLoading(false));
  }, [createOpen, fromBranchId]);


  // Auto-load available consignments when the add-consignment drawer opens
  useEffect(() => {
    if (addCnmtOpen) {
      setCnmtSearch("");
      void searchConsignments("");
    }
  }, [addCnmtOpen]);

  const openDetail = async (manifest: ManifestSummary) => {
    setDetailTab("details");
    setDetailOpen(true);
    setDetailLoading(true);
    try {
      const data = await transportApi.getManifest(manifest.id);
      setSelectedManifest(data as ManifestDetail);
    } catch (error) {
      message.error(getApiErrorMessage(error, "Failed to load manifest detail"));
    } finally {
      setDetailLoading(false);
    }
  };

  const reloadSelectedManifest = async () => {
    if (!selectedManifest) return;
    const data = await transportApi.getManifest(selectedManifest.id);
    setSelectedManifest(data as ManifestDetail);
  };

  const requestManifestEdit = async () => {
    if (!selectedManifest || !editRequestReason.trim()) {
      message.warning("Enter the reason for requesting edit access.");
      return;
    }
    await transportApi.requestManifestEdit(selectedManifest.id, editRequestReason.trim());
    message.success("Edit approval request submitted to Admin.");
    setEditRequestOpen(false);
    setEditRequestReason("");
    await reloadSelectedManifest();
  };

  const approveManifestEdit = async () => {
    if (!selectedManifest) return;
    await transportApi.approveManifestEdit(selectedManifest.id);
    message.success("Manifest edit approved.");
    await reloadSelectedManifest();
  };

  const openManifestEdit = () => {
    if (!selectedManifest) return;
    manifestEditForm.setFieldsValue({
      dispatchDate: dayjs(selectedManifest.dispatchDate),
      vehicleId: selectedManifest.vehicleId || undefined,
      driverName: selectedManifest.driverName ?? undefined,
      loadedBy: selectedManifest.loadedBy ?? undefined,
      transportMode: selectedManifest.transportMode,
      railwayReceiptNo: selectedManifest.railwayReceiptNo ?? undefined,
    });
    setManifestEditOpen(true);
  };

  const saveManifestEdit = async () => {
    if (!selectedManifest) return;
    const values = await manifestEditForm.validateFields();
    setManifestEditSaving(true);
    try {
      await transportApi.updateApprovedManifest(selectedManifest.id, {
        ...values,
        dispatchDate: values.dispatchDate.toISOString(),
      });
      message.success("Manifest updated. Edit approval has been consumed.");
      setManifestEditOpen(false);
      await reloadSelectedManifest();
      await loadManifests();
    } catch (error) {
      message.error(getApiErrorMessage(error, "Unable to update Manifest"));
    } finally {
      setManifestEditSaving(false);
    }
  };

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get("new") === "1") {
      const fromBranchId = Number(params.get("fromBranchId"));
      const manifestNo = params.get("manifestNo") ?? "";
      form.resetFields();
      form.setFieldsValue({
        manifestNo,
        fromBranchId: fromBranchId || undefined,
        dispatchDate: dayjs(),
        transportMode: "Road"
      });
      openCreateManifest();
      navigate("/manifests", { replace: true });
      return;
    }
    const manifestId = Number(params.get("manifestId"));
    if (!manifestId || loading || manifests.length === 0) return;
    const manifest = manifests.find((item) => item.id === manifestId);
    if (!manifest) return;
    void openDetail(manifest);
    navigate("/manifests", { replace: true });
  }, [location.search, loading, manifests, navigate]);

  const onCreate = async () => {
    let values: any;
    try {
      values = await form.validateFields();
    } catch (error: any) {
      const firstError = error?.errorFields?.[0];
      if (firstError?.name) {
        form.scrollToField(firstError.name, { block: "center" });
        requestAnimationFrame(() => form.getFieldInstance(firstError.name)?.focus?.());
      }
      message.warning(firstError?.errors?.[0] ?? "Complete the required manifest details.");
      return;
    }
    if (selectedConsignmentIds.length === 0) { message.warning("Select at least one consignment for the manifest."); setEligibleOpen(true); return; }
    const confirmed = await new Promise<boolean>((resolve) => Modal.confirm({
      title: "Create and lock this manifest?",
      content: "After saving, the selected consignments are assigned permanently and further changes are not allowed.",
      okText: "Create Manifest", cancelText: "Review", onOk: () => resolve(true), onCancel: () => resolve(false)
    }));
    if (!confirmed) return;
    const ctx = getDefaultContext();
    setCreateSaving(true);
    try {
      await transportApi.createManifest({
        ...ctx,
        manifestNo: !useAutomaticManifestNumber ? values.manifestNo?.trim() : undefined,
        useAutomaticNumber: useAutomaticManifestNumber,
        vehicleId: values.vehicleId,
        driverId: null,
        driverName: values.driverName ?? null,
        dispatchDate: (values.dispatchDate as dayjs.Dayjs).toISOString(),
        loadedBy: values.loadedBy ?? null,
        fromBranchId: values.fromBranchId,
        toBranchId: values.toBranchId,
        transportMode: values.transportMode ?? "Road",
        railwayReceiptNo: values.railwayReceiptNo ?? null,
        trainNo: values.trainNo ?? null,
        wagonNo: values.wagonNo ?? null,
        enteredBy: getEnteredByUser(),
        tempKey: createTempKey ?? undefined,
        consignments: selectedConsignmentIds.map((consignmentId) => ({ consignmentId, remarks: consignmentRemarks[consignmentId] ?? null })),
      });
      message.success("Manifest created successfully");
      // Clear tempKey without deleting — files are now bound server-side.
      setCreateTempKey(null);
      setCreateOpen(false);
      form.resetFields();
      await loadManifests();
    } catch (error) {
      message.error(getApiErrorMessage(error, "Failed to create manifest"));
    } finally {
      setCreateSaving(false);
    }
  };

  const searchConsignments = async (q: string) => {
    setCnmtSearching(true);
    try {
      const result = await transportApi.listConsignments({ q: q.trim() || undefined, pageSize: 50 });
      // Only show Booked consignments whose delivery station matches manifest's To Branch
      const toBranchId = selectedManifest?.toBranchId;
      setCnmtResults(result.data.filter((c: any) =>
        c.status === "Booked" && (!toBranchId || c.deliveryBranchId === toBranchId)
      ));
    } catch (error) {
      message.error(getApiErrorMessage(error, "Search failed"));
    } finally {
      setCnmtSearching(false);
    }
  };

  const addConsignmentToManifest = async (consignmentId: number) => {
    if (!selectedManifest) return;
    setAddCnmtSaving(true);
    try {
      await transportApi.addConsignmentToManifest(selectedManifest.id, {
        consignmentId,
        enteredBy: getEnteredByUser()
      });
      message.success("Consignment added to manifest");
      setAddCnmtOpen(false);
      setCnmtSearch("");
      setCnmtResults([]);
      // Reload detail
      const data = await transportApi.getManifest(selectedManifest.id);
      setSelectedManifest(data as ManifestDetail);
    } catch (error) {
      message.error(getApiErrorMessage(error, "Failed to add consignment"));
    } finally {
      setAddCnmtSaving(false);
    }
  };

  const removeConsignment = async (manifestId: number, consignmentId: number) => {
    try {
      await transportApi.removeConsignmentFromManifest(manifestId, consignmentId);
      message.success("Consignment removed");
      const data = await transportApi.getManifest(manifestId);
      setSelectedManifest(data as ManifestDetail);
    } catch (error) {
      message.error(getApiErrorMessage(error, "Failed to remove consignment"));
    }
  };

  const filteredManifests = manifests.filter(m => {
    const term = search.trim().toLowerCase();
    if (!term) return true;
    return [m.manifestNo, m.vehicleNo, m.driverName ?? "", m.loadedBy ?? ""].some(v =>
      v.toLowerCase().includes(term)
    );
  });

  const transportMode = Form.useWatch("transportMode", form);

  useEffect(() => {
    if (transportMode !== "Road" || !selectedVehicleId) { setOwnerName(""); return; }
    transportApi.getVehicleDetail(Number(selectedVehicleId))
      .then((vehicle) => setOwnerName(vehicle.ownerName || "Company Owned"))
      .catch(() => setOwnerName("Owner not available"));
  }, [selectedVehicleId, transportMode]);

  useEffect(() => {
    if (transportMode === "Rail") form.setFieldsValue({ vehicleId: undefined, driverId: undefined, driverName: undefined });
  }, [transportMode]);

  return (
    <Card className="panel-card">
      <div className="panel-title-row operations-data-only">
        <Space align="center" size={8}>
          <Typography.Title level={4} style={{ margin: 0 }}>Lorry Manifest Entry</Typography.Title>
          <span className="kbd-hint"><kbd>Ctrl</kbd>+<kbd>Shift</kbd>+<kbd>M</kbd></span>
        </Space>
        <Space>
          <span className="kbd-hint"><kbd>Alt</kbd>+<kbd>N</kbd></span>
          <Button
            ref={newBtnRef}
            type="primary"
            icon={<PlusOutlined />}
            className={search.trim() && filteredManifests.length === 0 ? "new-btn-highlight" : ""}
            onClick={() => openCreateManifest()}
          >
            New Manifest
          </Button>
        </Space>
      </div>

      <Space className="operations-data-only" style={{ width: "100%", justifyContent: "space-between", marginBottom: 16 }} wrap>
        <Input
          ref={searchInputRef}
          className="panel-search-input"
          prefix={<SearchOutlined />}
          placeholder="Search manifest no, vehicle, driver…  F3"
          allowClear
          value={search}
          onChange={e => setSearch(e.target.value)}
          onPressEnter={() => void loadManifests()}
          style={{ width: 360 }}
        />
        <Space>
          {search.trim() && filteredManifests.length === 0 && (
            <Typography.Text type="secondary">No results — press Alt+N to create</Typography.Text>
          )}
          <Button onClick={() => void loadManifests()}>Refresh <span className="kbd-hint"><kbd>F5</kbd></span></Button>
        </Space>
      </Space>

      <Table
        rowKey="id"
        loading={loading}
        dataSource={filteredManifests}
        size="small"
        pagination={{ pageSize: 25, showSizeChanger: true }}
        onRow={row => ({ onClick: () => void openDetail(row), style: { cursor: "pointer" } })}
        columns={[
          { title: "#", width: 50, render: (_, __, i) => i + 1 },
          { title: "Manifest No (Challan)", dataIndex: "manifestNo", width: 160 },
          {
            title: "Date",
            dataIndex: "dispatchDate",
            width: 100,
            render: (v: string) => dayjs(v).format("DD/MM/YYYY")
          },
          { title: "Lorry No", dataIndex: "vehicleNo", width: 120 },
          { title: "Driver", dataIndex: "driverName", width: 130 },
          { title: "Mode", dataIndex: "transportMode", width: 70 },
          { title: "RR No", dataIndex: "railwayReceiptNo", width: 100 },
          { title: "Loaded By", dataIndex: "loadedBy", width: 120 },
          { title: "Cnmts", dataIndex: "consignmentCount", width: 65, align: "right" },
          { title: "Pkgs", dataIndex: "totalPackages", width: 60, align: "right" },
          {
            title: "Weight",
            dataIndex: "totalWeight",
            width: 80,
            align: "right",
            render: (v: number) => v.toFixed(3)
          }
        ]}
      />

      {/* ── Create Manifest Drawer ── */}
      <StaticDrawer
        title="New Lorry Manifest"
        width="100%"
        open={createOpen}
        onClose={cancelCreateManifest}
        footer={<div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}><span className="kbd-hint"><kbd>Ctrl</kbd>+<kbd>S</kbd> Save &nbsp;·&nbsp; <kbd>Alt</kbd>+<kbd>W</kbd> Close</span><div style={{ display: "flex", gap: 8 }}><Button onClick={cancelCreateManifest}>Cancel</Button><SaveActionButton saving={createSaving} idleText="Save Manifest" savingText="Saving..." onClick={() => void onCreate()} /></div></div>}
        destroyOnHidden
        maskClosable={false}
      >
        <Form form={form} layout="vertical" initialValues={{ transportMode: "Road", dispatchDate: dayjs() }}>
          {/* Row 1: From Branch | To Branch */}
          <Row gutter={12}>
            <Col span={12}>
              <Form.Item name="fromBranchId" label="From Branch / Station" rules={[{ required: true }]}>
                <LookupSelect fetcher={branchFetcher} placeholder="Search branch..."
                  addNewLabel="Branch"
                  seededOptions={branchSeeds}
                  onAddNew={(q) => { setBranchPrefill(q); setBranchTarget("fromBranchId"); setBranchCreateOpen(true); }} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="toBranchId"
                label="To Branch / Station"
                dependencies={["fromBranchId"]}
                rules={[
                  { required: true, message: "Select the destination branch" },
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      return value && value === getFieldValue("fromBranchId")
                        ? Promise.reject(new Error("From and To Branch / Station must be different."))
                        : Promise.resolve();
                    },
                  }),
                ]}
              >
                <LookupSelect fetcher={branchFetcher} placeholder="Search branch..."
                  addNewLabel="Branch"
                  seededOptions={branchSeeds}
                  onAddNew={(q) => { setBranchPrefill(q); setBranchTarget("toBranchId"); setBranchCreateOpen(true); }} />
              </Form.Item>
            </Col>
          </Row>

          {/* Row 2: Date | Manifest No (stationery) */}
          <Row gutter={12}>
            <Col span={manualManifestNumberEnabled ? 8 : 8}>
              <Form.Item name="dispatchDate" label="Date" rules={[{ required: true }]}>
                <DatePicker style={{ width: "100%" }} format="DD/MM/YYYY" />
              </Form.Item>
            </Col>
            {(
              <Col span={16}>
                <Form.Item
                  name="manifestNo"
                  label={<Space size={8}>Manifest / Challan No{useAutomaticManifestNumber && <Tag color="blue">Automatic sequence applied</Tag>}</Space>}
                  rules={useAutomaticManifestNumber ? [] : [
                    { required: true, message: "Enter manifest number" },
                    { pattern: /^[0-9]+$/, message: "Manifest number must be numeric" },
                  ]}
                  extra={useAutomaticManifestNumber
                    ? "No stationery range is assigned; this number comes from the automatic document sequence."
                    : fromBranchId
                    ? availableManifestRanges.length > 0
                      ? `Assigned range: ${availableManifestRanges.map((x) => `${x.startNumber}-${x.endNumber}`).join(", ")}`
                      : "No active Manifest range is assigned to the selected From Branch."
                    : "Select the From Branch to load its assigned range."}
                >
                  <Input readOnly={useAutomaticManifestNumber} maxLength={30} inputMode="numeric" placeholder="Enter reserved manifest number" />
                </Form.Item>
              </Col>
            )}
          </Row>

          {/* Road-only lorry details */}
          {transportMode === "Road" && <Row gutter={12}>
            <Col span={8}>
              <Form.Item name="vehicleId" label="Lorry No" rules={[{ required: true, message: "Select lorry" }]}>
                <LookupSelect fetcher={vehicleFetcher} placeholder="Search vehicle no..."
                  addNewLabel="Vehicle"
                  seededOptions={vehicleSeeds}
                  onAddNew={(q) => { setVehiclePrefill(q); setVehicleCreateOpen(true); }} />
              </Form.Item>
            </Col>
            <Col span={8}><Form.Item label="Owner Name"><Input readOnly value={ownerName} placeholder="Loaded with lorry" /></Form.Item></Col>
            <Col span={8}><Form.Item name="driverName" label="Driver Name"><Input placeholder="Enter driver name" /></Form.Item></Col>
          </Row>}

          {/* Row 4: Mode | Loaded By */}
          <Row gutter={12}>
            <Col span={12}>
              <Form.Item name="transportMode" label="Mode" rules={[{ required: true }]}>
                <Select options={TRANSPORT_MODES} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="loadedBy" label="Loaded By">
                <Input />
              </Form.Item>
            </Col>
          </Row>

          {/* Rail-only fields */}
          {transportMode === "Rail" && (
            <>
              <Divider>Rail Details</Divider>
              <Row gutter={12}>
                <Col span={8}>
                  <Form.Item name="railwayReceiptNo" label="RR No (Railway Receipt)">
                    <Input />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item name="trainNo" label="Train No">
                    <Input />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item name="wagonNo" label="Wagon No">
                    <Input />
                  </Form.Item>
                </Col>
              </Row>
            </>
          )}
        </Form>

        <Card size="small" title={`Selected Consignments (${selectedConsignmentIds.length})`} extra={<Button onClick={() => { setDraftSelectedConsignmentIds(selectedConsignmentIds); setEligibleOpen(true); }}>Add / Edit Consignments</Button>}>
          <Table className="entry-inline-table" style={{ display: "block" }} rowKey="id" size="small" pagination={false} locale={{ emptyText: "No consignments selected" }} dataSource={selectedConsignments} columns={[
            { title: "Bilty / LR No", dataIndex: "consignmentNo" }, { title: "Destination", dataIndex: "destination" },
            { title: "Consignor", dataIndex: "consignor", ellipsis: true }, { title: "Consignee", dataIndex: "consignee", ellipsis: true },
            { title: "Pkgs", dataIndex: "packages", width: 70 }, { title: "Weight", dataIndex: "weight", width: 90 },
            { title: "Remark", width: 240, render: (_, row) => <Input value={consignmentRemarks[row.id] ?? ""} onChange={(event) => setConsignmentRemarks((current) => ({ ...current, [row.id]: event.target.value }))} placeholder="Optional remark" /> },
            { title: "Action", width: 90, align: "center" as const, render: (_, row) => <Button danger type="text" icon={<DeleteOutlined />} onClick={() => {
              setSelectedConsignmentIds((current) => current.filter((id) => id !== row.id));
              setSelectedConsignments((current) => current.filter((item) => item.id !== row.id));
              setConsignmentRemarks((current) => { const next = { ...current }; delete next[row.id]; return next; });
            }}>Remove</Button> }
          ]} />
        </Card>

        {/* Pre-save attachments — bound server-side when the manifest is created */}
        {createTempKey && (
          <div style={{ marginTop: 16, paddingTop: 16, borderTop: "1px solid var(--app-bg)" }}>
            <EntityAttachmentsPanel
              entityType="manifest"
              entityId={0}
              tempKey={createTempKey}
              entityLabel="New Manifest"
              branchId={fromBranchId ? Number(fromBranchId) : undefined}
            />
          </div>
        )}
      </StaticDrawer>

      {/* ── Manifest Detail Drawer ── */}
      <Modal open={eligibleOpen} title="Select Consignments for Manifest" width={1050} onCancel={() => setEligibleOpen(false)} onOk={() => {
        setSelectedConsignmentIds(draftSelectedConsignmentIds);
        setSelectedConsignments(eligibleRows.filter((row) => draftSelectedConsignmentIds.includes(Number(row.id))));
        setConsignmentRemarks((current) => Object.fromEntries(Object.entries(current).filter(([id]) => draftSelectedConsignmentIds.includes(Number(id)))));
        setEligibleOpen(false);
      }} okText={`Add ${draftSelectedConsignmentIds.length} Selected`}>
        <Typography.Text type="secondary">Only booked consignments from the selected From Station that are not assigned to another manifest are shown.</Typography.Text>
        <Table rowKey="id" size="small" loading={{ spinning: eligibleLoading, tip: "Loading pending consignments…" }} locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No pending consignments are available for this branch." /> }} dataSource={eligibleRows} pagination={{ pageSize: 15 }} rowSelection={{ selectedRowKeys: draftSelectedConsignmentIds, onChange: (keys) => setDraftSelectedConsignmentIds(keys.map(Number)), preserveSelectedRowKeys: true }} columns={[
          { title: "Bilty / LR No", dataIndex: "consignmentNo", width: 140 }, { title: "Date", dataIndex: "bookingDate", width: 110, render: (value: string) => dayjs(value).format("DD/MM/YYYY") },
          { title: "Origin", dataIndex: "origin" }, { title: "Destination", dataIndex: "destination" }, { title: "Consignor", dataIndex: "consignor", ellipsis: true }, { title: "Consignee", dataIndex: "consignee", ellipsis: true },
          { title: "Pkgs", dataIndex: "packages", width: 60 }, { title: "Remark", width: 220, render: (_, row) => <Input disabled={!draftSelectedConsignmentIds.includes(Number(row.id))} value={consignmentRemarks[row.id] ?? ""} onChange={(event) => setConsignmentRemarks((current) => ({ ...current, [row.id]: event.target.value }))} placeholder="Optional remark" /> }
        ]} />
      </Modal>

      <StaticDrawer
        title={selectedManifest ? `Manifest — ${selectedManifest.manifestNo}` : "Manifest Detail"}
        width="100%"
        open={detailOpen}
        onClose={() => { setDetailOpen(false); setSelectedManifest(null); }}
        footer={
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Space>
              {selectedManifest && detailTab === "details" && (
                <>
                  {selectedManifest.canApproveEdit && <Button type="primary" style={{ background: "#389e0d", borderColor: "#389e0d" }} onClick={() => void approveManifestEdit()}>Approve Edit Request</Button>}
                  {selectedManifest.editApprovalStatus === "Pending" && !selectedManifest.canApproveEdit
                    ? <Button disabled style={{ color: "#ad6800", background: "#fff7e6", borderColor: "#ffd591" }}>Approval Pending</Button>
                    : <Button type="primary" icon={<EditOutlined />} disabled={selectedManifest.hasArrival} onClick={() => selectedManifest.canEdit ? openManifestEdit() : setEditRequestOpen(true)}>
                        Edit Manifest
                      </Button>}
                  <Button onClick={() => navigate("/gate-pass")}>Gate Pass</Button>
                  <Button icon={<PrinterOutlined />} onClick={async () => {
                    const report = await transportApi.getManifestReport(selectedManifest!.id);
                    printManifestStationery(report);
                  }}>Print</Button>
                </>
              )}
            </Space>
            <Button onClick={() => { setDetailOpen(false); setSelectedManifest(null); }}>Close</Button>
          </div>
        }
        destroyOnHidden
        maskClosable={false}
      >
        {detailLoading ? (
          <Typography.Text type="secondary">Loading manifest...</Typography.Text>
        ) : selectedManifest ? (
          <Tabs
            activeKey={detailTab}
            onChange={setDetailTab}
            size="small"
            style={{ marginTop: -8 }}
            items={[
              {
                key: "details",
                label: "Consignments",
                children: (
                  <Space direction="vertical" style={{ width: "100%" }}>
                    <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
                      {isAdmin && selectedManifest.editApprovalStatus === "Pending" && (
                        <Button type="primary" style={{ background: "#389e0d", borderColor: "#389e0d" }} onClick={() => void approveManifestEdit()}>Approve User Edit Request</Button>
                      )}
                      {isAdmin ? (
                        <Button type="primary" icon={<EditOutlined />} disabled={selectedManifest.hasArrival} onClick={openManifestEdit}>Edit Manifest</Button>
                      ) : selectedManifest.editApprovalStatus === "Pending" ? (
                        <Button disabled style={{ color: "#ad6800", background: "#fff7e6", borderColor: "#ffd591" }}>Approval Pending</Button>
                      ) : selectedManifest.editApprovalStatus === "Approved" && selectedManifest.canEdit ? (
                        <Button type="primary" icon={<EditOutlined />} disabled={selectedManifest.hasArrival} onClick={openManifestEdit}>Edit Manifest</Button>
                      ) : (
                        <Button type="primary" icon={<EditOutlined />} disabled={selectedManifest.hasArrival} onClick={() => setEditRequestOpen(true)}>Apply for Edit Approval</Button>
                      )}
                    </div>
                    {selectedManifest.editApprovalStatus !== "None" && (
                      <Alert
                        type={selectedManifest.editApprovalStatus === "Approved" ? "success" : "warning"}
                        showIcon
                        message={selectedManifest.editApprovalStatus === "Approved" ? "Edit approved — one edit is allowed" : "Manifest edit approval is pending"}
                        description={selectedManifest.editRequestReason || undefined}
                      />
                    )}
                    {/* Header */}
                    <Descriptions bordered size="small" column={4}>
                      <Descriptions.Item label="Manifest No (Challan)" span={1}>
                        <strong>{selectedManifest.manifestNo}</strong>
                      </Descriptions.Item>
                      <Descriptions.Item label="Date">
                        {dayjs(selectedManifest.dispatchDate).format("DD/MM/YYYY")}
                      </Descriptions.Item>
                      <Descriptions.Item label="Lorry No">{selectedManifest.vehicleNo}</Descriptions.Item>
                      <Descriptions.Item label="Mode">{selectedManifest.transportMode}</Descriptions.Item>
                      <Descriptions.Item label="Driver">{selectedManifest.driverName ?? "—"}</Descriptions.Item>
                      <Descriptions.Item label="Loaded By">{selectedManifest.loadedBy ?? "—"}</Descriptions.Item>
                      {selectedManifest.railwayReceiptNo && (
                        <Descriptions.Item label="RR No" span={2}>
                          {selectedManifest.railwayReceiptNo}
                        </Descriptions.Item>
                      )}
                    </Descriptions>

                    <Divider style={{ margin: "8px 0" }} />

                    {/* Consignment grid */}
                    <Table
                      rowKey="consignmentId"
                      size="small"
                      dataSource={selectedManifest.consignments}
                      scroll={{ x: 1200 }}
                      pagination={false}
                      summary={() => {
                        const cnmts = selectedManifest.consignments;
                        return (
                          <Table.Summary.Row style={{ fontWeight: 600 }}>
                            <Table.Summary.Cell index={0} colSpan={3}>TOTAL</Table.Summary.Cell>
                            <Table.Summary.Cell index={3} />
                            <Table.Summary.Cell index={4} />
                            <Table.Summary.Cell index={5} />
                            <Table.Summary.Cell index={6} align="right">
                              {cnmts.reduce((s, r) => s + r.packages, 0)}
                            </Table.Summary.Cell>
                            <Table.Summary.Cell index={7} align="right">
                              {cnmts.reduce((s, r) => s + r.actualWeight, 0).toFixed(3)}
                            </Table.Summary.Cell>
                            <Table.Summary.Cell index={8} colSpan={4} />
                          </Table.Summary.Row>
                        );
                      }}
                      columns={[
                        { title: "#", width: 40, render: (_, __, i) => i + 1 },
                        { title: "Consignment No", dataIndex: "consignmentNo", width: 140 },
                        {
                          title: "Date",
                          dataIndex: "bookingDate",
                          width: 90,
                          render: (v: string) => dayjs(v).format("DD-MM-YY")
                        },
                        { title: "Destination", dataIndex: "destination", width: 100 },
                        { title: "Consignor", dataIndex: "consignor", ellipsis: true },
                        { title: "Consignee", dataIndex: "consignee", ellipsis: true },
                        { title: "Pkgs", dataIndex: "packages", width: 55, align: "right" },
                        {
                          title: "Weight",
                          dataIndex: "actualWeight",
                          width: 75,
                          align: "right",
                          render: (v: number) => v.toFixed(3)
                        },
                        { title: "Goods", dataIndex: "goodsName", width: 110, ellipsis: true },
                        { title: "E-Way Bill", dataIndex: "eWayBillNo", width: 110, ellipsis: true },
                        { title: "Pvt. Mark", dataIndex: "privateMark", width: 90, ellipsis: true },
                        {
                          title: "Payment",
                          dataIndex: "paymentType",
                          width: 70,
                          align: "center",
                          render: (v: string) => (
                            <Tag color={PAYMENT_COLORS[v] ?? "default"} style={{ fontSize: 10, padding: "0 3px" }}>
                              {v === "ToBeBilled" ? "TBB" : v.toUpperCase()}
                            </Tag>
                          )
                        }
                      ]}
                    />
                  </Space>
                )
              },
              {
                key: "documents",
                label: "Documents",
                children: (
                  <EntityAttachmentsPanel
                    entityType="manifest"
                    entityId={selectedManifest.id}
                    entityLabel={selectedManifest.manifestNo}
                  />
                )
              }
            ]}
          />
        ) : null}

        {/* Add Consignment sub-drawer */}
        <StaticDrawer
          title="Add Consignment to Manifest"
          width="100%"
          open={addCnmtOpen}
          onClose={() => { setAddCnmtOpen(false); setCnmtSearch(""); setCnmtResults([]); }}
          destroyOnHidden
          maskClosable={false}
        >
          <Space.Compact style={{ width: "100%", marginBottom: 12 }}>
            <Input
              placeholder="Search by Bilty / LR no, origin, destination..."
              value={cnmtSearch}
              onChange={e => setCnmtSearch(e.target.value)}
              onPressEnter={() => void searchConsignments(cnmtSearch)}
            />
            <Button
              type="primary"
              icon={<SearchOutlined />}
              loading={cnmtSearching}
              onClick={() => void searchConsignments(cnmtSearch)}
            >
              Search
            </Button>
          </Space.Compact>
          <Table
            rowKey="id"
            size="small"
            loading={cnmtSearching}
            dataSource={cnmtResults}
            pagination={false}
            columns={[
              { title: "No", dataIndex: "consignmentNo", width: 130 },
              {
                title: "Date", dataIndex: "bookingDate", width: 85,
                render: (v: string) => v ? dayjs(v).format("DD/MM/YY") : "—"
              },
              { title: "Origin", dataIndex: "origin" },
              { title: "Dest", dataIndex: "destination" },
              { title: "Pkgs", dataIndex: "totalPackages", width: 50, align: "right" },
              {
                title: "",
                width: 80,
                render: (_, row: any) => (
                  <Button
                    size="small"
                    type="primary"
                    loading={addCnmtSaving}
                    onClick={() => void addConsignmentToManifest(row.id)}
                  >
                    Add
                  </Button>
                )
              }
            ]}
          />
        </StaticDrawer>
      </StaticDrawer>

      <Modal title="Request Admin Approval to Edit Manifest" open={editRequestOpen} onCancel={() => setEditRequestOpen(false)} onOk={() => void requestManifestEdit()} okText="Submit Request">
        <Typography.Paragraph type="secondary">Saved Manifests are locked. Explain why this Manifest needs to be edited.</Typography.Paragraph>
        <Input.TextArea rows={4} maxLength={500} showCount value={editRequestReason} onChange={(event) => setEditRequestReason(event.target.value)} placeholder="Reason for edit request" />
      </Modal>

      <Modal title={selectedManifest ? `Edit Manifest — ${selectedManifest.manifestNo}` : "Edit Manifest"} open={manifestEditOpen} onCancel={() => setManifestEditOpen(false)} onOk={() => void saveManifestEdit()} confirmLoading={manifestEditSaving} okText="Save Approved Edit" width={760}>
        <Alert type="info" showIcon message="This approval permits one edit. Saving will lock the Manifest again." style={{ marginBottom: 16 }} />
        <Form form={manifestEditForm} layout="vertical">
          <Row gutter={12}>
            <Col span={12}><Form.Item name="dispatchDate" label="Date" rules={[{ required: true }]}><DatePicker style={{ width: "100%" }} /></Form.Item></Col>
            <Col span={12}><Form.Item name="transportMode" label="Mode" rules={[{ required: true }]}><Select options={TRANSPORT_MODES} /></Form.Item></Col>
          </Row>
          {editTransportMode === "Road" ? (
            <Row gutter={12}>
              <Col span={12}><Form.Item name="vehicleId" label="Lorry No" rules={[{ required: true }]}><LookupSelect fetcher={vehicleFetcher} seededOptions={vehicleSeeds} placeholder="Search vehicle no..." /></Form.Item></Col>
              <Col span={12}><Form.Item name="driverName" label="Driver Name"><Input /></Form.Item></Col>
            </Row>
          ) : (
            <Form.Item name="railwayReceiptNo" label="RR No (Railway Receipt)"><Input /></Form.Item>
          )}
          <Form.Item name="loadedBy" label="Loaded By"><Input /></Form.Item>
        </Form>
      </Modal>

      {/* ── Inline create modals ── */}
      <VehicleCreateModal
        open={vehicleCreateOpen}
        prefillName={vehiclePrefill}
        onClose={() => setVehicleCreateOpen(false)}
        onCreated={(item) => {
          setVehicleSeeds(prev => [item, ...prev.filter(x => x.id !== item.id)]);
          form.setFieldValue("vehicleId", item.id);
          setVehicleCreateOpen(false);
        }}
      />
      <DriverCreateModal
        open={driverCreateOpen}
        prefillName={driverPrefill}
        onClose={() => setDriverCreateOpen(false)}
        onCreated={(item) => {
          setDriverSeeds(prev => [item, ...prev.filter(x => x.id !== item.id)]);
          form.setFieldValue("driverId", item.id);
          setDriverCreateOpen(false);
        }}
      />
      <BranchCreateModal
        open={branchCreateOpen}
        prefillName={branchPrefill}
        onClose={() => setBranchCreateOpen(false)}
        onCreated={(item) => {
          setBranchSeeds(prev => [item, ...prev.filter(x => x.id !== item.id)]);
          form.setFieldValue(branchTarget, item.id);
          setBranchCreateOpen(false);
        }}
      />

    </Card>
  );
};
